'use client'

import { getAgencyUser } from '@/app/(main)/agency/(auth)/helper'
import { getAxiosApiClientByUser } from './axiosInstance'

// Add simple caching for agency ID
let cachedAgencyId: string | null = null
let lastFetchTime: number = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds

// Generic API request handler
const makeApiRequest = async <T>(
  endpoint: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any,
  method: 'get' | 'post' | 'put' | 'delete'
): Promise<T | null> => {
  try {
    let response

    const apiClient = await getAxiosApiClientByUser()

    switch (method) {
      case 'get':
        response = await apiClient.get(endpoint, { params: payload })
        break
      case 'post':
        response = await apiClient.post(endpoint, payload)
        break
      case 'put':
        response = await apiClient.put(endpoint, payload)
        break
      case 'delete':
        response = await apiClient.delete(endpoint, { params: payload })
        break
      default:
        throw new Error(`Unsupported method: ${method}`)
    }

    // Check if the response data contains a detail field (which might be an error)
    if (response.data && response.data.detail) {
      // console.log('API returned detail message:', response.data.detail)
      // Instead of treating this as success, create an error-like response
      return {
        success: false,
        error: response.data.detail
      } as unknown as T
    }

    return response.data as T
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(`API Error: ${error.message}`)
    if (error.response) {
      console.error('Response data:', error.response.data)

      // If the response has error details, use them
      if (
        error.response.data &&
        (error.response.data.detail || error.response.data.error)
      ) {
        return {
          success: false,
          error: error.response.data.detail || error.response.data.error
        } as unknown as T
      }
    }

    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    } as unknown as T
  }
}

export interface SenderEmail {
  id: string
  email: string
  createdAt: string
}

export interface CreateSenderEmailResponse {
  success: boolean
  email?: SenderEmail
  message?: string
  error?: string
}
export interface EmailCredits {
  emailQuota: number
  emailUsed: number
  emailRemaining: number
  senderQuota: number
  senderUsed: number
  senderRemaining: number
  resetDate: string
  plan: string
}

export interface EmailCredits1 {
  quotas: {
    email: {
      total: number
      used: number
      remaining: number
    }
    landingPages: {
      total: number
      used: number
      remaining: number
    }
    imageGeneration: {
      total: number
      used: number
      remaining: number
    }
    imageEditing: {
      total: number
      used: number
      remaining: number
    }
    senderEmails: {
      total: number
      used: number
      remaining: number
    }
    publishedPages: {
      total: number
      used: number
      remaining: number
    }
  }
}

// Improved getAgencyId with caching
const getAgencyId = async () => {
  const currentTime = Date.now()

  // Return cached value if it exists and is not expired
  if (cachedAgencyId && currentTime - lastFetchTime < CACHE_DURATION) {
    return cachedAgencyId
  }

  try {
    const currentUser = getAgencyUser()
    if (!currentUser?.agencyUserId) {
      throw new Error('No agency user found')
    }

    // Update cache
    cachedAgencyId = currentUser.agencyUserId.agencyId
    lastFetchTime = currentTime

    return cachedAgencyId
  } catch (error) {
    console.error('Error getting agency ID:', error)
    throw error
  }
}

export const getEmailCredits = async (): Promise<EmailCredits | null> => {
  try {
    const agencyId = await getAgencyId()

    const response = await makeApiRequest<EmailCredits>(
      '/credit/email-credits',
      { agencyId },
      'get'
    )

    // console.log('Get email credits response:', response)
    return response
  } catch (error) {
    console.error('Error fetching email credits:', error)
    return null
  }
}

export const getEmailsMyUsage = async (): Promise<EmailCredits | null> => {
  try {
    const agencyId = await getAgencyId()

    const response = await makeApiRequest<EmailCredits>(
      '/credit/my-usage',
      { agencyId },
      'get'
    )

    // console.log('Get email credits response:', response)
    return response
  } catch (error) {
    console.error('Error fetching email credits:', error)
    return null
  }
}

export const createSenderEmail = async (
  prefix: string
): Promise<CreateSenderEmailResponse> => {
  try {
    const agencyId = await getAgencyId()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await makeApiRequest<any>(
      '/sender/create_sender_email',
      { prefix, agencyId },
      'post'
    )

    // console.log('Raw API response:', response)

    // Check for successful response (has ID)
    if (response && response.id) {
      return {
        success: true,
        email: {
          id: response.id,
          email: response.email,
          createdAt: response.createdAt
        },
        message: 'Email created successfully'
      }
    }

    // Check for error response from backend in various formats
    if (response) {
      // Check for different possible error fields
      const errorMessage = response.error || response.detail || response.message
      if (errorMessage) {
        return {
          success: false,
          error: errorMessage
        }
      }
    }

    // Fallback error message if no specific error was provided
    return {
      success: false,
      error: 'Failed to create email: Invalid response from server'
    }
  } catch (error) {
    console.error('Error creating sender email:', error)

    // If the error object has response data, check for different error fields
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorResponse = (error as any)?.response?.data
    if (errorResponse) {
      const errorMessage =
        errorResponse.error || errorResponse.detail || errorResponse.message
      if (errorMessage) {
        return {
          success: false,
          error: errorMessage
        }
      }
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create email'
    }
  }
}

export const getSenderEmails = async (): Promise<SenderEmail[]> => {
  try {
    const agencyId = await getAgencyId()

    const response = await makeApiRequest<SenderEmail[]>(
      '/sender/get_sender_emails',
      { agencyId },
      'get'
    )

    return response || []
  } catch (error) {
    console.error('Error fetching sender emails:', error)
    return []
  }
}

export const topUpEmailCredits = async (
  packageType: string
): Promise<{ success: boolean; error?: string; url?: string }> => {
  try {
    const agencyId = await getAgencyId()

    const response = await makeApiRequest<{ success: boolean; error?: string }>(
      '/credit/email-credits/top-up',
      { package: packageType, agencyId },
      'post'
    )

    return (
      response || {
        success: false,
        url: '',
        error: 'Failed to top up credits: No response from server'
      }
    )
  } catch (error) {
    console.error('Error topping up email credits:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to top up credits'
    }
  }
}

export const updateSenderEmail = async (
  id: string,
  prefix: string
): Promise<CreateSenderEmailResponse> => {
  try {
    const agencyId = await getAgencyId()

    const response = await makeApiRequest<CreateSenderEmailResponse>(
      `/sender/update_sender_email/${id}`,
      { prefix, agencyId },
      'put'
    )

    return (
      response || {
        success: false,
        error: 'Failed to update email: No response from server'
      }
    )
  } catch (error) {
    console.error('Error updating sender email:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update email'
    }
  }
}

export const deleteSenderEmail = async (
  id: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const agencyId = await getAgencyId()

    const response = await makeApiRequest<{
      success: boolean
      message?: string
      error?: string
    } | null>(
      `/sender/delete_sender_email/${id}`,
      { emailId: id, agencyId },
      'delete'
    )

    return (
      response || {
        success: false,
        error: 'Failed to delete email: No response from server'
      }
    )
  } catch (error) {
    console.error('Error deleting sender email:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete email'
    }
  }
}

// Add a method to manually clear the cache if needed
export const clearAgencyIdCache = () => {
  cachedAgencyId = null
  lastFetchTime = 0
}
